<!-- eslint-disable complexity -->
<script setup lang="ts">
import { TreeExpandedKeys, TreeSelectionKeys, TreeState } from 'primevue/tree';
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  shallowRef,
  watch,
} from 'vue';

import BaseTree from 'lib/components/basetree/BaseTree.vue';
import { TreeNode } from 'lib/components/basetree/BaseTree.vue.d';
import eventBus from 'lib/event-bus';
import { useWangsVueConfig } from 'lib/plugins/WangsVue';
import mergePropsWithDefaults from 'lib/utils/mergePropsWithDefaults.util';

import { filterNodeKeys, flattenTreeNodeChildren } from './helpers';
import { TreeEmits, TreeProps, TreeSlots } from './Tree.vue.d';

const props = withDefaults(defineProps<TreeProps>(), {
  selectLastNode: false,
  autoSelectAll: false,
  disableNodeAll: false,
  selectedKeys: undefined,
  selectionMode: 'checkbox',
  useOption: false,
  includeNodeAllKey: false,
  shouldEmitEventOnChange: false,
  propagateSelection: true,
});

const emit = defineEmits<TreeEmits>();

const slots = defineSlots<TreeSlots>();

const { defaultProps } = useWangsVueConfig('Tree');
const config = computed(() => mergePropsWithDefaults(props, defaultProps));

onMounted(() => {
  eventBus.on('tree:update-tree-nodes', handleUpdateTreeNodes);
  updateTreeFilter(props.filter);
  fetchTreeList();
});

onBeforeUnmount(() => {
  eventBus.off('tree:update-tree-nodes', handleUpdateTreeNodes);
});

const isLoading = ref<boolean>(true);
const treeNodeID = ref<string>(new Date().getTime().toString());
const tree = ref<TreeState>();
const treeNodes = ref<TreeNode[]>([]);
const flatNodes = ref<TreeNode[]>([]);
const parents = ref<{ [x: string]: string[] }>({});
const isExpandedAll = ref<boolean>(false);
const expandedKeys = ref<TreeExpandedKeys>({ '-1': true });
const showDisposableGroups = ref<boolean>(false);

const selectedKeysModel = computed({
  get: () => props.selectedKeys ?? {},
  set: (keys: TreeSelectionKeys) => {
    emit('update:selectedKeys', keys);
  },
});

/**
 * Temporary Store the initial checked node
 */
const initialChecked = shallowRef<TreeSelectionKeys>({});

/**
 * Sets the initial checked state for the tree nodes based on the selected nodes and selection mode.
 * If the selection mode is "single", sets the checked state for the first selected node.
 * If the selection mode is "checkbox", traverses the tree and sets the checked state for each node based on the selected nodes.
 *
 * @returns {void}
 */
const setInitialChecked = (keys: string[]): void => {
  // Only set the node checked if the props.selectedKeys is string array from API Response data
  if (props.selectionMode === 'checkbox') {
    const traverse = (nodes: TreeNode[], parentKey?: string): void => {
      nodes.forEach((node) => {
        const checked = !!keys?.includes(node.key as string);
        initialChecked.value[node.key] = { checked };

        if (node.children?.length) traverse(node.children, node.key as string);
        if (parentKey) setInitialParent(parentKey);
      });
    };

    traverse(treeNodes.value);
  } else {
    const key = keys?.[0];

    if (key) {
      initialChecked.value = { [key]: true };
    }
  }
};

const isStringArray = (value: unknown): boolean => {
  return (
    Array.isArray(value) && value.every((item) => typeof item === 'string')
  );
};

/**
 * Sets the checked and partial checked state for the parent node based on the checked state of its children.
 *
 * @param {string} parentKey - The key of the parent node.
 * @returns {void}
 */
const setInitialParent = (parentKey: string): void => {
  const isAllChildChecked = isAllChildrenChecked(parentKey);
  const checked = initialChecked.value[parentKey].checked || isAllChildChecked;
  const partialChecked = !isAllChildChecked && isSomeChildrenChecked(parentKey);

  initialChecked.value[parentKey] = {
    // It should be checked when not partial checked, when it partially checked, the checked value should be false
    checked: !partialChecked && checked,
    partialChecked,
  };
};

/**
 * Checks if all children of a parent node are checked.
 *
 * @param {string} parentKey - The key of the parent node.
 * @returns {boolean} - True if all children are checked, false otherwise.
 */
const isAllChildrenChecked = (parentKey: string): boolean => {
  return parents.value[parentKey]?.every((child: string) => {
    const childKey = JSON.stringify(child);
    return initialChecked.value[childKey]?.checked;
  });
};

/**
 * Checks if some children(s) of a parent node are checked or partially checked.
 *
 * @param {string} parentKey - The key of the parent node.
 * @returns {boolean} - True if some children are checked or partially checked, false otherwise.
 */
const isSomeChildrenChecked = (parentKey: string): boolean => {
  return parents.value[parentKey]?.some((child: string) => {
    const childKey = JSON.stringify(child);

    return (
      initialChecked.value[childKey]?.checked ||
      initialChecked.value[childKey]?.partialChecked
    );
  });
};

/**
 * Maps the response data from the backend to a TreeNode array.
 * Sets the parent-child relationship between nodes and stores the parent keys in the "parents" ref object.
 *
 * @param {TreeNode[]} datas - The response data from the backend.
 * @returns {TreeNode[]} - The mapped TreeNode array.
 */
const responseMapping = (datas: TreeNode[]): TreeNode[] => {
  return datas.map((data) => {
    data.key = data.key ?? data._id;
    if (data.children?.length) {
      parents.value = {
        ...parents.value,
        [data.key.toString()]: data.children.map(
          (child) => child.key as string,
        ),
      };

      data.children = responseMapping(data.children);
    }

    if (props.disableNodeWhen) data.selectable = !props.disableNodeWhen(data);
    return data;
  });
};

const handleUpdateTreeNodes = (): void => {
  (async (): Promise<void> => {
    emit('update:selectedKeys', {});
    await fetchTreeList(false);
    updateSelectedTreeNodes();
  })();
};

const fetchTreeList = async (initialFetch: boolean = true): Promise<void> => {
  try {
    isLoading.value = true;

    const { data } = (await config.value.fetchTree(
      props.type,
      props.params,
    )) ?? { data: [] }; // FetchTree may return undefined when it fails, return emptry tree to prevent error execution

    if (data) {
      const mappedNode = responseMapping(data) as TreeNode[];
      flatNodes.value = flattenTreeNodeChildren(mappedNode) as TreeNode[];
      treeNodes.value = mappedNode;

      sessionStorage.setItem(`${props.type}Tree`, JSON.stringify(mappedNode));
      sessionStorage.setItem(
        `flatten${props.type}nodes`,
        JSON.stringify(flatNodes.value),
      );
    }

    if (props.selectLastNode && props.selectionMode === 'single')
      await nextTick(selectLastSelectedNode);

    if (props.autoSelectAll && props.selectionMode === 'checkbox')
      await nextTick(selectAllNode);
  } finally {
    if (isStringArray(props.selectedKeys)) {
      setInitialChecked(props.selectedKeys as string[]);
      selectedKeysModel.value = initialChecked.value;
    }

    if (!initialFetch) eventBus.emit('tree:tree-updated');
    isLoading.value = false;
  }
};

/**
 * Get the last selected Node by user to trigger 'sidebarNodeSelected'.
 *
 * If the last node does not exist, click the 'All' group.
 * We need to select the node manualy at first to trigger event.
 *
 * Function from Sidebar Tree moved here.
 */
const selectLastSelectedNode = async (): Promise<void> => {
  const lastSelectedNode = sessionStorage.getItem(
    `${props.type.toLowerCase()}Node`,
  );

  const allNode: TreeNode = treeNodes.value?.[0];
  /**
   * When props.disableNodeAll, the Node All should not be selected programmatically by option selectLastNode
   * Select the first child of All instead, it will always exist
   *
   * In some cases, the 'All' node still can be selected.
   */
  const firstChildOfAll: TreeNode | undefined = allNode?.children?.[0];

  const parsedLastNode: TreeNode | null = lastSelectedNode
    ? JSON.parse(lastSelectedNode)
    : null;

  const isAllNode = parsedLastNode?.key == -1;
  const fallbackNodeWhenLastNodeEmpty = props.disableNodeAll
    ? firstChildOfAll
    : allNode;

  const lastNode =
    isAllNode && props.disableNodeAll
      ? // When last node is All, and disableNodeExist, it should select the firstChild of all
        firstChildOfAll
      : (parsedLastNode ?? fallbackNodeWhenLastNodeEmpty);

  if (lastNode?.key != null && lastNodeExistOnTree(lastNode?.key)) {
    selectedKeysModel.value = { [lastNode?.key]: true };

    // Restore expanded keys before select last node
    const expandedKeysStored = sessionStorage.getItem(
      `${props.type.toLowerCase()}NodeExpandedKeys`,
    );

    if (expandedKeysStored) expandedKeys.value = JSON.parse(expandedKeysStored);

    setLastSelectedNode(lastNode);
  }
};

/**
 * Check whether the Last Selected node still exist on TreeNodes or not.
 * It is used to prevent error loading node detail if the node deleted or user role changed.
 *
 * @param key Node key
 */
const lastNodeExistOnTree = (key: string | number): boolean => {
  return flatNodes.value.some((node) => node.key === key);
};

const setLastSelectedNode = (node: TreeNode): void => {
  emit('nodeSelect', node);
  if (!props.shouldEmitEventOnChange) return;

  sessionStorage.setItem(
    `${props.type.toLowerCase()}Node`,
    JSON.stringify(node),
  );

  sessionStorage.setItem(
    `${props.type.toLowerCase()}NodeExpandedKeys`,
    JSON.stringify(expandedKeys.value),
  );
};

const selectAllNode = (): void => {
  const temmpSelection: TreeSelectionKeys = {};

  flatNodes.value.forEach((node) => {
    temmpSelection[node.key] = {
      checked: true,
      partialChecked: false,
    };
  });

  selectedKeysModel.value = temmpSelection;
};

const expandAll = (): void => {
  isExpandedAll.value = true;
  for (const node of treeNodes.value) {
    expandNode(node);
  }

  expandedKeys.value = { ...expandedKeys.value };
};

const expandNode = (node: TreeNode): void => {
  if (node.children?.length) {
    expandedKeys.value[node.key ?? ''] = true;

    for (const child of node.children) {
      expandNode(child);
    }
  }
};

const collapseAll = (): void => {
  expandedKeys.value = { '-1': true };
  isExpandedAll.value = false;
};

const updateSelectedTreeNodes = (): void => {
  if (props.selectedKeys) {
    const selectedKeys = Array.isArray(props.selectedKeys)
      ? props.selectedKeys
      : Object.keys(
          filterNodeKeys(props.selectedKeys, props.includeNodeAllKey),
        );

    const selectedNodes = flatNodes.value.filter((node) =>
      selectedKeys.includes(node._id),
    );

    if (props.shouldEmitEventOnChange) {
      sessionStorage.setItem(
        `${props.type.toLowerCase()}Keys`,
        JSON.stringify(selectedKeys),
      );
      eventBus.emit('tree:selection-changed', {
        keys: JSON.stringify(selectedKeys),
      });
    }

    emit('update:selectedTreeNodes', selectedNodes);
  }
};

const updateTreeFilter = (query: string): void => {
  if (query?.length) expandAll();
  else expandNodeAll();
};

const onNodeSelected = (event: TreeNode): void => {
  if (!props.readonly) {
    setLastSelectedNode(event);
  }
};

/**
 * Expand the All node to show all Parent Group
 */
const expandNodeAll = (): void => {
  expandedKeys.value = { '-1': true };
};

watch(
  () => props.filter,
  (query: string) => {
    updateTreeFilter(query);
  },
);

watch(
  () => [props.selectedKeys, props.type],
  async (newValue, oldValue) => {
    if (newValue[1] !== oldValue[1]) {
      emit('update:selectedKeys', {});
      await fetchTreeList();
    }
    updateSelectedTreeNodes();
  },
);

defineExpose({
  treeNodes,
  flatNodes,
  isLoading,
  showDisposableGroups,
  isExpandedAll,
  collapseAll,
  expandAll,
  expandNodeAll,
  fetchTreeList,
});
</script>

<template>
  <BaseTree
    :id="treeNodeID"
    ref="tree"
    v-bind="$attrs"
    v-model:expanded-keys="expandedKeys"
    v-model:selection-keys="selectedKeysModel"
    :class="{ readonly: props.readonly }"
    :disable-keys="disableKeys"
    :disable-node-all="props.disableNodeAll"
    :disable-node-when="disableNodeWhen"
    :exact-disable-keys="exactDisableKeys"
    :exact-disable-node-when="exactDisableNodeWhen"
    :filter-text="filter"
    :list-labels-no-option="listLabelsNoOption"
    :loading="isLoading"
    :node-selected-when="nodeSelectedWhen"
    :propagate-selection="propagateSelection"
    :selection-mode="props.selectionMode"
    :show-disposable-groups="showDisposableGroups"
    :tree-node-menus="treeNodeMenus"
    :use-option="useOption"
    :value="treeNodes"
    @node-collapse="isExpandedAll = false"
    @node-select="onNodeSelected"
    @node-unselect="emit('nodeUnselect', $event)"
    @toggle-menu="emit('toggleMenu', $event)"
    filter-by="name"
  >
    <template #default="{ node, checked, disabled }" v-if="slots.default">
      <slot :checked="checked" :disabled="disabled" :node="node" />
    </template>
  </BaseTree>
</template>

<style></style>
